import { useState, useEffect } from 'react';
import supabase from '../supabase';
import { theme, font, inputStyle, labelStyle, outlineButton, ghostButton, pillButton } from './adminTheme';
import { uploadToB2 } from '../utils/uploadToB2';
import { useAdminLang } from './AdminLangContext';
import '../design-system.css';

const T = {
    fr: {
        existing: 'Campagnes existantes',
        loading: 'Chargement...',
        empty: 'Aucune campagne.',
        active: '· actif',
        activate: 'Activer',
        edit: 'Éditer',
        delete: 'Suppr.',
        editingTitle: (year) => `Éditer campagne ${year}`,
        newTitle: 'Nouvelle campagne',
        newInstead: '+ Nouvelle plutôt',
        year: 'Année',
        heroPhoto: 'Photo hero',
        uploading: 'Upload en cours...',
        taglineEnFr: 'Tagline (EN/FR)',
        taglineVi: 'Tagline (Vietnamien)',
        story: 'Récit de la campagne',
        photographerName: 'Nom du photographe',
        username: 'Username',
        location: 'Lieu',
        saving: 'Sauvegarde...',
        update: 'Mettre à jour',
        create: 'Créer la campagne',
        yearRequired: 'Année requise.',
        uploadError: (msg) => `Erreur upload : ${msg}`,
        photoUploaded: 'Photo uploadée',
        saveError: (msg) => `Erreur : ${msg}`,
        saved: 'Campagne sauvegardée',
        confirmDelete: 'Supprimer cette campagne définitivement ?',
    },
    en: {
        existing: 'Existing campaigns',
        loading: 'Loading...',
        empty: 'No campaigns.',
        active: '· active',
        activate: 'Activate',
        edit: 'Edit',
        delete: 'Delete',
        editingTitle: (year) => `Edit campaign ${year}`,
        newTitle: 'New campaign',
        newInstead: '+ New instead',
        year: 'Year',
        heroPhoto: 'Hero photo',
        uploading: 'Uploading...',
        taglineEnFr: 'Tagline (EN/FR)',
        taglineVi: 'Tagline (Vietnamese)',
        story: 'Campaign story',
        photographerName: 'Photographer name',
        username: 'Username',
        location: 'Location',
        saving: 'Saving...',
        update: 'Update',
        create: 'Create campaign',
        yearRequired: 'Year is required.',
        uploadError: (msg) => `Upload error: ${msg}`,
        photoUploaded: 'Photo uploaded',
        saveError: (msg) => `Error: ${msg}`,
        saved: 'Campaign saved',
        confirmDelete: 'Permanently delete this campaign?',
    },
};

const EMPTY_FORM = {
    id: null,
    year: new Date().getFullYear(),
    image_url: '',
    tagline: 'A story through pictures.',
    tagline_vi: 'một câu chuyện qua những bức ảnh',
    story: '',
    photographer_name: '',
    photographer_username: '',
    location: '',
    is_active: false,
};

function AdminCampaigns() {
    const { lang } = useAdminLang();
    const t = T[lang] || T.fr;
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(EMPTY_FORM);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [statusMsg, setStatusMsg] = useState('');

    const fetchCampaigns = async () => {
        setLoading(true);
        const { data, error } = await supabase.from('campaigns').select('*').order('year', { ascending: false });
        if (!error && data) setCampaigns(data);
        setLoading(false);
    };

    useEffect(() => { fetchCampaigns(); }, []);

    const handleEdit = (c) => setForm(c);
    const handleNew = () => setForm(EMPTY_FORM);

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        try {
            const fileName = `campaign-${form.year}-${Date.now()}.${file.name.split('.').pop()}`;
            const url = await uploadToB2(`campaign-photos/${fileName}`, file);
            setForm(f => ({ ...f, image_url: url }));
            setStatusMsg(t.photoUploaded);
        } catch (err) {
            setStatusMsg(t.uploadError(err.message));
        }
        setUploading(false);
    };

    const handleSave = async () => {
        if (!form.year) { setStatusMsg(t.yearRequired); return; }
        setSaving(true);

        const payload = {
            year: Number(form.year),
            image_url: form.image_url,
            tagline: form.tagline,
            tagline_vi: form.tagline_vi,
            story: form.story,
            photographer_name: form.photographer_name,
            photographer_username: form.photographer_username,
            location: form.location,
        };

        let error;
        if (form.id) {
            ({ error } = await supabase.from('campaigns').update(payload).eq('id', form.id));
        } else {
            ({ error } = await supabase.from('campaigns').insert(payload));
        }

        if (error) setStatusMsg(t.saveError(error.message));
        else {
            setStatusMsg(t.saved);
            handleNew();
            fetchCampaigns();
        }
        setSaving(false);
    };

    const handleSetActive = async (id) => {
        await supabase.from('campaigns').update({ is_active: false }).neq('id', id);
        await supabase.from('campaigns').update({ is_active: true }).eq('id', id);
        fetchCampaigns();
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t.confirmDelete)) return;
        await supabase.from('campaigns').delete().eq('id', id);
        fetchCampaigns();
    };

    return (
        <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap', alignItems: 'flex-start' }}>

            <div style={{ flex: '1 1 320px', minWidth: '280px' }}>
                <p style={{ ...labelStyle, marginBottom: '14px' }}>{t.existing}</p>

                {loading ? (
                    <p style={{ color: theme.textMuted, fontFamily: font, fontSize: '12px' }}>{t.loading}</p>
                ) : campaigns.length === 0 ? (
                    <p style={{ color: theme.textMuted, fontFamily: font, fontSize: '12px' }}>{t.empty}</p>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {campaigns.map(c => (
                            <div key={c.id} style={{
                                display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px',
                                background: theme.card,
                                border: c.is_active ? `1px solid ${theme.text}` : `1px solid ${theme.border}`,
                                borderRadius: 0,
                            }}>
                                <div style={{ width: '44px', height: '44px', borderRadius: 0, overflow: 'hidden', flexShrink: 0, background: theme.bg }}>
                                    {c.image_url && <img src={c.image_url} alt={c.year} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <p style={{ margin: 0, fontFamily: font, fontSize: '13px', color: theme.text }}>
                                        {c.year} {c.is_active && <span style={{ color: theme.textMuted, fontSize: '10px' }}>{t.active}</span>}
                                    </p>
                                    <p style={{ margin: 0, fontFamily: font, fontSize: '10.5px', color: theme.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {c.tagline}
                                    </p>
                                </div>
                                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                                    {!c.is_active && (
                                        <button className="wlink" onClick={() => handleSetActive(c.id)} style={{ ...outlineButton, padding: '5px 10px', fontSize: '10.5px' }}>{t.activate}</button>
                                    )}
                                    <button className="wlink" onClick={() => handleEdit(c)} style={{ ...outlineButton, padding: '5px 10px', fontSize: '10.5px' }}>{t.edit}</button>
                                    <button className="wlink" onClick={() => handleDelete(c.id)} style={{ ...outlineButton, padding: '5px 10px', fontSize: '10.5px', color: theme.textMuted }}>{t.delete}</button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div style={{ flex: '1 1 400px', minWidth: '320px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                    <p style={{ ...labelStyle, marginBottom: 0 }}>{form.id ? t.editingTitle(form.year) : t.newTitle}</p>
                    {form.id && <button className="wlink" onClick={handleNew} style={ghostButton}>{t.newInstead}</button>}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <div>
                        <label style={labelStyle}>{t.year}</label>
                        <input type="number" value={form.year} onChange={e => setForm(f => ({ ...f, year: e.target.value }))} style={inputStyle} />
                    </div>

                    <div>
                        <label style={labelStyle}>{t.heroPhoto}</label>
                        {form.image_url && (
                            <div style={{ width: '100%', aspectRatio: '16/9', borderRadius: 0, overflow: 'hidden', marginBottom: '8px', background: theme.bg }}>
                                <img src={form.image_url} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </div>
                        )}
                        <input type="file" accept="image/*" onChange={handleUpload} disabled={uploading} style={{ fontSize: '11px', color: theme.textMuted, fontFamily: font }} />
                        {uploading && <p style={{ fontSize: '10px', color: theme.textMuted, margin: '4px 0 0' }}>{t.uploading}</p>}
                    </div>

                    <div>
                        <label style={labelStyle}>{t.taglineEnFr}</label>
                        <input value={form.tagline} onChange={e => setForm(f => ({ ...f, tagline: e.target.value }))} style={inputStyle} />
                    </div>

                    <div>
                        <label style={labelStyle}>{t.taglineVi}</label>
                        <input value={form.tagline_vi} onChange={e => setForm(f => ({ ...f, tagline_vi: e.target.value }))} style={inputStyle} />
                    </div>

                    <div>
                        <label style={labelStyle}>{t.story}</label>
                        <textarea rows={5} value={form.story} onChange={e => setForm(f => ({ ...f, story: e.target.value }))} style={{ ...inputStyle, resize: 'vertical' }} />
                    </div>

                    <div style={{ display: 'flex', gap: '12px' }}>
                        <div style={{ flex: 1 }}>
                            <label style={labelStyle}>{t.photographerName}</label>
                            <input value={form.photographer_name} onChange={e => setForm(f => ({ ...f, photographer_name: e.target.value }))} style={inputStyle} />
                        </div>
                        <div style={{ flex: 1 }}>
                            <label style={labelStyle}>{t.username}</label>
                            <input value={form.photographer_username} onChange={e => setForm(f => ({ ...f, photographer_username: e.target.value }))} style={inputStyle} />
                        </div>
                    </div>

                    <div>
                        <label style={labelStyle}>{t.location}</label>
                        <input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} style={inputStyle} />
                    </div>

                    <button className="wlink" onClick={handleSave} disabled={saving} style={{ ...pillButton, marginTop: '4px' }}>
                        {saving ? t.saving : form.id ? t.update : t.create}
                    </button>

                    {statusMsg && <p style={{ fontSize: '11px', color: theme.textMuted, fontFamily: font, margin: 0 }}>{statusMsg}</p>}
                </div>
            </div>
        </div>
    );
}

export default AdminCampaigns;